/**
 * ProductCatalog - Browse products by category
 * Features:
 * - Category tabs/filters
 * - Product grid with cards
 * - Color swatch hover to change product image
 * - Fun entrance animations
 */
import React, { useState, useMemo, useEffect } from 'react';
import { COLORS, FONT_FAMILY, SHOP_SPACING, CARD_STYLES } from '../ShopStyles';
import { PRODUCT_CATEGORIES, getProductsByCategory, getProductImageForVariant } from '../data/products';
import StarRating from '../components/StarRating';

const ProductCatalog = ({
  onNavigate,
  onSelectProduct,
  generatedImage,
  petName,
  initialCategory = 'all',
  scrollToProductId = null,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'all');
  const [hoveredProduct, setHoveredProduct] = useState(null);
  // Track hovered variant per product: { productId: variantId }
  const [hoveredVariants, setHoveredVariants] = useState({});
  
  // Inject entrance animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'product-catalog-animations';
    styleSheet.textContent = `
      @keyframes slideUp {
        0% { 
          opacity: 0; 
          transform: translateY(30px) scale(0.95); 
        }
        100% { 
          opacity: 1; 
          transform: translateY(0) scale(1); 
        }
      }
      .product-card-animate {
        animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        opacity: 0;
      }
      .color-dot:hover {
        transform: scale(1.2);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
      }
      /* Fun button hover effects */
      .fun-button {
        transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
      }
      .fun-button:hover {
        transform: translateY(-3px) scale(1.03) !important;
        box-shadow: 0 8px 25px rgba(147, 51, 234, 0.4) !important;
      }
      .fun-button:active {
        transform: translateY(-1px) scale(1.01) !important;
      }
      .category-tab:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(147, 51, 234, 0.15) !important;
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      const existing = document.getElementById('product-catalog-animations');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    setSelectedCategory(initialCategory || 'all');
  }, [initialCategory]);

  // Filter products by category
  const filteredProducts = useMemo(() => {
    return getProductsByCategory(selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    if (!scrollToProductId) return;
    const elId = `product-card-${scrollToProductId}`;
    const timer = setTimeout(() => {
      document.getElementById(elId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    return () => clearTimeout(timer);
  }, [scrollToProductId, selectedCategory, filteredProducts.length]);

  // Handle product selection
  const handleProductClick = (product) => {
    onSelectProduct?.(product);
    onNavigate?.('product-detail', { productId: product.id });
  };

  // Get the current image for a product (based on hovered variant or default)
  const getProductImage = (product) => {
    const hoveredVariantId = hoveredVariants[product.id];
    if (hoveredVariantId) {
      return getProductImageForVariant(product, hoveredVariantId);
    }
    return product.mockupImage;
  };

  // Handle variant dot hover
  const handleVariantHover = (productId, variantId) => {
    setHoveredVariants(prev => ({
      ...prev,
      [productId]: variantId
    }));
  };

  // Handle variant dot leave
  const handleVariantLeave = (productId) => {
    setHoveredVariants(prev => {
      const newState = { ...prev };
      delete newState[productId];
      return newState;
    });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🛍️ Shop Products</h1>
        <p style={styles.subtitle}>
          {generatedImage 
            ? `Put ${petName || 'your pet'}'s portrait on amazing products!`
            : 'Browse our products and customize with your pet photo'
          }
        </p>
      </div>

      {/* Show pet preview if available */}
      {generatedImage && (
        <div style={styles.petPreview}>
          <img src={generatedImage} alt={petName} style={styles.petPreviewImage} />
          <span style={styles.petPreviewText}>
            Customizing with {petName || 'your pet'}
          </span>
        </div>
      )}

      {/* Category Tabs */}
      <div style={styles.categoryTabs}>
        {PRODUCT_CATEGORIES.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className="category-tab"
            style={{
              ...styles.categoryTab,
              ...(selectedCategory === category.id ? styles.categoryTabActive : {}),
            }}
          >
            <span style={styles.categoryEmoji}>{category.emoji}</span>
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Product Grid */}
      <div style={styles.productGrid}>
        {filteredProducts.map((product, index) => (
          <div
            key={product.id}
            id={`product-card-${product.id}`}
            className="product-card-animate"
            style={{
              ...styles.productCard,
              animationDelay: `${index * 0.06}s`,
              transform: hoveredProduct === product.id ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
              boxShadow: hoveredProduct === product.id ? CARD_STYLES.shadowHover : CARD_STYLES.shadow,
            }}
            onMouseEnter={() => setHoveredProduct(product.id)}
            onMouseLeave={() => {
              setHoveredProduct(null);
              handleVariantLeave(product.id);
            }}
            onClick={() => handleProductClick(product)}
          >
            {/* Popular Badge */}
            {product.popular && (
              <div style={styles.popularBadge}>⭐ Popular</div>
            )}
            
            {/* Product Image - Changes based on hovered variant */}
            <div style={styles.productImageWrapper}>
              <img
                src={getProductImage(product)}
                alt={product.name}
                style={styles.productImage}
                loading="lazy"
              />
              
              {/* Hover Overlay */}
              <div style={{
                ...styles.productOverlay,
                opacity: hoveredProduct === product.id ? 1 : 0,
              }}>
                <button style={styles.customizeButton}>
                  🎨 Customize
                </button>
              </div>
            </div>
            
            {/* Product Info */}
            <div style={styles.productInfo}>
              <h3 style={styles.productName}>{product.name}</h3>
              
              {/* Star Rating */}
              {product.rating && (
                <div style={{ marginBottom: '6px' }}>
                  <StarRating 
                    rating={product.rating} 
                    reviewCount={product.reviewCount}
                    size="small"
                    compact={true}
                  />
                </div>
              )}
              
              <p style={styles.productPrice}>${product.price.toFixed(2)}</p>
              
              {/* Variant Colors - Hoverable to change image */}
              {product.variants.length > 1 && (
                <div style={styles.variantDots}>
                  {product.variants.slice(0, 4).map((variant) => {
                    const isWhite = variant.color === '#ffffff' || variant.color === 'white';
                    const isHovered = hoveredVariants[product.id] === variant.id;
                    return (
                      <div
                        key={variant.id}
                        className="color-dot"
                        style={{
                          ...styles.variantDot,
                          background: variant.color === 'transparent' ? 
                            'linear-gradient(135deg, #fff 0%, #ddd 100%)' : 
                            variant.color,
                          border: isWhite ? '2px solid #d1d5db' : isHovered ? '2px solid #9333ea' : '2px solid transparent',
                          transform: isHovered ? 'scale(1.2)' : 'scale(1)',
                        }}
                        title={variant.name}
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          handleVariantHover(product.id, variant.id);
                        }}
                        onMouseLeave={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    );
                  })}
                  {product.variants.length > 4 && (
                    <span style={styles.moreVariants}>+{product.variants.length - 4}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>📦</span>
          <p style={styles.emptyText}>No products in this category yet</p>
        </div>
      )}

      {/* Back Button */}
      <button
        onClick={() => onNavigate?.('home')}
        style={styles.backButton}
      >
        ← Back to Home
      </button>
    </div>
  );
};

const styles = {
  container: {
    padding: `${SHOP_SPACING.lg} 0`,
  },
  header: {
    textAlign: 'center',
    marginBottom: SHOP_SPACING.xl,
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#333',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.sm,
  },
  subtitle: {
    fontSize: '18px',
    color: '#666',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  petPreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SHOP_SPACING.md,
    background: 'rgba(255,255,255,0.9)',
    borderRadius: '16px',
    padding: SHOP_SPACING.md,
    marginBottom: SHOP_SPACING.xl,
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.05)',
  },
  petPreviewImage: {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    objectFit: 'cover',
  },
  petPreviewText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    fontFamily: FONT_FAMILY,
  },
  categoryTabs: {
    display: 'flex',
    gap: SHOP_SPACING.sm,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: SHOP_SPACING.xl,
  },
  categoryTab: {
    display: 'flex',
    alignItems: 'center',
    gap: SHOP_SPACING.xs,
    padding: '10px 18px',
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    borderRadius: '25px',
    border: '2px solid #e5e7eb',
    background: '#fff',
    color: '#666',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
    outline: 'none',
  },
  categoryTabActive: {
    background: '#9333ea',
    borderColor: '#9333ea',
    color: '#fff',
  },
  categoryEmoji: {
    fontSize: '18px',
  },
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: SHOP_SPACING.lg,
    marginBottom: SHOP_SPACING.xl,
  },
  productCard: {
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
  },
  popularBadge: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    background: '#f59e0b',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 10px',
    borderRadius: '20px',
    zIndex: 1,
    fontFamily: FONT_FAMILY,
  },
  productImageWrapper: {
    position: 'relative',
    aspectRatio: '1',
    overflow: 'hidden',
    background: '#f5f5f5',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  productOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.3s ease',
  },
  customizeButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    background: '#fff',
    color: COLORS.accent,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  },
  productInfo: {
    padding: SHOP_SPACING.md,
  },
  productName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '4px',
  },
  productPrice: {
    fontSize: '20px',
    fontWeight: '700',
    color: COLORS.accent,
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.sm,
  },
  variantDots: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  variantDot: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  moreVariants: {
    fontSize: '12px',
    color: '#666',
    fontFamily: FONT_FAMILY,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '60px',
    display: 'block',
    marginBottom: SHOP_SPACING.md,
    opacity: 0.5,
  },
  emptyText: {
    fontSize: '18px',
    color: '#666',
    fontFamily: FONT_FAMILY,
  },
  backButton: {
    display: 'block',
    margin: '0 auto',
    background: 'transparent',
    border: 'none',
    color: '#7E52C7',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    padding: SHOP_SPACING.md,
  },
};

export default ProductCatalog;
